import { Component, inject, ChangeDetectionStrategy, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { trigger, state, style, transition, animate, AnimationEvent } from '@angular/animations';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

import { ComponentService } from '../../services/component.service';
import { ChatPanelComponent } from './chat-panel/chat-panel.component';

@Component({
  selector: 'app-chatbot',
  imports: [CommonModule, MatIconModule, MatButtonModule, TranslateModule, ChatPanelComponent],
  templateUrl: './chatbot.component.html',
  styleUrl: './chatbot.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('panelAnimation', [
      state('closed', style({ transform: 'translateY(100%)', opacity: 0, pointerEvents: 'none' })),
      state('open', style({ transform: 'translateY(0)', opacity: 1, pointerEvents: 'auto' })),
      transition('closed => open', animate('200ms ease-out')),
      transition('open => closed', animate('150ms ease-in')),
    ]),
  ],
})
export class ChatbotComponent implements OnInit, OnDestroy {
  private readonly componentService = inject(ComponentService);
  private readonly translateService = inject(TranslateService);
  private readonly destroy$ = new Subject<void>();

  public isOpen = false;
  public showPanel = false;
  public isVisible = false;
  public openLabel = '';
  public closeLabel = '';

  public ngOnInit(): void {
    this.isVisible = this.componentService.isFeatureActive('VEV_IQ_CHATBOT');
    this.updateLabels();
    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateLabels();
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public toggleChat(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.showPanel = true;
    }
  }

  public onAnimationDone(event: AnimationEvent): void {
    if (event.toState === 'closed') {
      this.showPanel = false;
    }
  }

  private updateLabels(): void {
    this.openLabel = this.translateService.instant('chatbot.open_chat');
    this.closeLabel = this.translateService.instant('chatbot.close_chat');
  }
}
